// WP-6.4 (M29 AD-4, LOCKED §2.34, S-32): "Can I afford this?" — a plain four-part answer card
// (can/advisable/impact/timing) for an on-demand amount+purpose check. WP-6.5 (M29 AD-5, S-32)
// adds a collapsed-by-default [Why?] expander below the answer — inputs → assumptions →
// confidence → trade-offs, template-rendered from the response already on hand.
import { parseAmountInput, type CurrencyCode } from '@pfos/engine';
import { useState } from 'react';
import { useCheckAffordability } from '../lib/affordability';
import { buildAffordabilityWhy } from '../lib/why';
import { Amount } from './Amount';
import { AmountField, Field, inputClass, primaryButtonInlineClass, selectClass } from './FormKit';
import { WhyExpander } from './WhyExpander';

const CURRENCIES: readonly CurrencyCode[] = ['PKR', 'USD', 'EUR', 'GBP'];

export function AffordabilityBox(): React.JSX.Element {
  const [purpose, setPurpose] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<CurrencyCode>('PKR');
  const [error, setError] = useState<string | null>(null);
  const checkMutation = useCheckAffordability();

  const handleSubmit = (): void => {
    if (purpose.trim() === '') {
      setError('Enter a purpose');
      return;
    }
    const parsed = parseAmountInput(amount, { defaultCurrency: currency });
    if (!parsed.ok) {
      setError('Enter a valid amount');
      return;
    }
    setError(null);
    checkMutation.mutate({
      amountMinor: parsed.money.minorUnits,
      currency,
      purpose: purpose.trim(),
    });
  };

  const answer = checkMutation.data;

  return (
    <div className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface p-4">
      <p className="mb-3 text-sm font-medium text-ink">Can I afford this?</p>
      <Field label="Purpose" htmlFor="afford-purpose">
        <input
          id="afford-purpose"
          className={inputClass}
          value={purpose}
          onChange={(e) => {
            setPurpose(e.target.value);
          }}
          placeholder="e.g. a new laptop"
        />
      </Field>
      <Field label="Amount" htmlFor="afford-amount">
        <AmountField id="afford-amount" currency={currency} value={amount} onChange={setAmount} />
      </Field>
      <Field label="Currency" htmlFor="afford-currency">
        <select
          id="afford-currency"
          className={selectClass}
          value={currency}
          onChange={(e) => {
            setCurrency(e.target.value as CurrencyCode);
          }}
        >
          {CURRENCIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>
      {error !== null && <p className="mb-2 text-sm text-danger">{error}</p>}
      {checkMutation.isError && (
        <p className="mb-2 text-sm text-danger">{checkMutation.error.message}</p>
      )}
      <button
        type="button"
        className={primaryButtonInlineClass}
        disabled={checkMutation.isPending}
        onClick={handleSubmit}
      >
        Check affordability
      </button>

      {answer !== undefined && (
        <div className="mt-4 border-t border-border pt-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <p className="text-sm text-muted">Can you pay right now?</p>
              <p className="font-medium text-ink">
                {answer.canPayNow ? 'Yes' : 'No'} — spendable now:{' '}
                <Amount currency={answer.currency} minorUnits={answer.spendableNowMinor} />
              </p>
            </div>
            <div>
              <p className="text-sm text-muted">Is it advisable?</p>
              <p className="font-medium text-ink">
                {answer.advisable ? 'Yes' : 'No'} — already spoken for:{' '}
                <Amount currency={answer.currency} minorUnits={answer.protectedFloorMinor} />
              </p>
            </div>
            <div>
              <p className="text-sm text-muted">Effect on future commitments</p>
              <p className="font-medium text-ink">
                <Amount currency={answer.currency} minorUnits={answer.futureImpactMinor} />
                {answer.futureImpactMinor < 0 ? ' eaten into protected money' : ' of headroom left'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted">Would waiting help?</p>
              <p className="font-medium text-ink">
                {answer.waitingWouldHelp ? (
                  <>
                    Yes —{' '}
                    <Amount currency={answer.currency} minorUnits={answer.upcomingInflowMinor} /> in
                    confirmed incoming money would close the gap.
                  </>
                ) : (
                  'No'
                )}
              </p>
            </div>
          </div>
          <WhyExpander sections={buildAffordabilityWhy(answer)} />
        </div>
      )}
    </div>
  );
}
